// 訓練期程：明細展開＋學員表單明細 modal＋產表單 modal＋匯入區（批次 15，B7-1，issue #13）。
//
// legacy 對應（行號逐檔親讀 2026-09-24，基準＝ePortfolio_work/.../training/planInst/）：
//   - 明細展開＝DataTable row.child（planInstList_js.jsp:128-136 details-control→showDetail）；
//     子表樣板＝planInstList.jsp:93-136 #planInstDetailSample（兩列表頭：rowspan/colspan，
//     表單 colspan2 表頭含連結 showFormDetail('form','{{planInstId}}','{{studentIdName}}')）；
//     列渲染 :380-497（genTRClass/genBtn/genPeriod/genPhaseIdName/genSubPhaseIdName/mentor/
//     clinical/ward/form/percentages）。
//   - 啟停（genActiveBtn :299-325／activePlan :330-366）：confirm
//     `是否確定{啟用|停用}期程[{phaseTargetIdName}]?`→updateIsActiveAjax→info＋reShowDetail。
//   - 表單明細 modal＝planInstModalFormList.jsp:10-44 #formModal（說明列 :21 逐字；表 thead
//     訓練期間(20%)/訓練階段(20%)/訓練科別(15%)/表單名稱(45%)）；showFormDetail :87-109
//     （title＝studentName(text-success)＋` 表單明細 `）；chip＝genFormText :130-159
//     （formId 空→btn-default 不可點／isDone==1→btn-success／否則 btn-danger；已發出可點→
//     showFillForm :255-262 window.open→雛形提示呈現，B4-1 先例）；發送表單＝genFormTextExtra
//     :160-170（dispatchType 1(手動)/2(自動+手動)→external-link 圓鈕）。
//   - 產表單 modal＝#generateFormModal :49-82（title=fromTemplateName；應完成日 datepicker
//     readonly maxlength10；formFlowListTable＝templateForFiller.jsp:23-40：流程(序號)/角色/
//     填寫者/指導醫師教師(非必填)(Student 才有)；Student 鎖定本人 setDisabledSelect :174-190；
//     Mentor/Clinical 帳號池）；generateForm :201-249（填寫者空白→0 允許不填 20180402；
//     產生表單→成功 info＋hide＋重開表單明細；雛形＝成功訊息，無真後端）。
//   - 匯入區＝planInstList.jsp:49-81 #importForm（匯入期程資料 label＋uploadFile .xlsx＋
//     期程匯入 importPlanInst(0)＋#tsghImportRow 訓練官及住院醫師用每月期程匯入 importPlanInst(1)
//     （批次 49 裁決 6：jobTypeId 值 '1'（系統管理）或空字串視為隱藏，其餘顯示——初始態
//     display:none 對齊 legacy；planInstFormSearchPlanInst.jsp:42-46）＋使用說明＋範本下載＋li 逐字）。
//     批次 49 起本元件由 PlanInstListBody（complexBody）掛進獨立 importForm（泛用模板時代
//     的 bulkActions 容器已棄用）；importPlanInst :507-570 驗證鏈：planTarget 空/<1→
//     `請選擇 訓練計畫`（showInfoDialog）→uploadFile 空→`請選擇匯入的檔案`→confirm
//     `訓練計畫 : {text}<br/><br/>是否確定要匯入?`→成功 common.showAlertDialog(desc)＋doQuery
//     （雛形＝成功訊息，批次 14 academicMOM 先例；:558 `#modalPlanTargetDialog` hide＝無此
//     modal 的 copy-paste＝legacy-known-issue 照實不畫）。
//   - 範本下載（myDownloadTemplate :576-616）：planTarget 空→`請選擇計畫訓練`（字面值 alert）；
//     isGroup==1 且組別空→`請選擇組別 `（尾空格）；confirm `訓練計畫 : {text}`＋`</br> 組別 :
//     {text}`＋`<br/><br/>是否確定要下載該計畫之範本?`→$.fileDownload→Blob 下載
//     （批次 9 downloadTemplateBlob 先例【雛形值待實機校準】）。
//   - 完成份數連結（openFormList :627-650）→`/form/planFormList?phaseInstId=..`：跨頁參數相依
//     →雛形提示呈現（B4-1 先例），登記 KNOWN-NOT-DONE。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import type { Row } from '@/mocks/types';
import type { BehaviorFlowContext } from '../templates/page-behaviors';
import {
  COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
  TRAINING_CLINICAL_OPTIONS,
  TRAINING_FORM_FLOW_SEED,
  TRAINING_JOB_TYPE_OPTIONS,
  TRAINING_MENTOR_OPTIONS,
  TRAINING_PHASE_TARGET_OPTIONS,
  TRAINING_STUDENT_OPTIONS,
} from '@/mocks/resource-rules';

/** showPercentage（planInstList_js.jsp:532-536）：100 藍／0 黑／其餘紅（逐字語意）。 */
export function showPercentageOf(p: string): ReactElement {
  if (p === '100') {
    return <span className="text-blue-600">100%</span>;
  }
  if (p === '0') {
    return <span className="text-black">0%</span>;
  }
  return <span className="text-red-600">{p}%</span>;
}

// ---------------------------------------------------------------------------
// 明細展開（details-control／row.child）
// ---------------------------------------------------------------------------

/** 明細欄的展開控制項（legacy td.details-control 圖示；React 以 +/- 按鈕呈現）。 */
export function PlanInstDetailCell({
  pageId,
  row,
  uiKey,
  ctx,
}: {
  pageId: string;
  row: Row;
  uiKey: string;
  ctx: BehaviorFlowContext;
}): ReactElement {
  const rowId = String(row.id ?? '');
  const expanded = ctx.expandedRowIds?.has(rowId) === true;
  return (
    <Button
      uiId={`${pageId}-row-${uiKey}-detail-toggle`}
      variant="default"
      size="sm"
      type="button"
      onClick={() => ctx.toggleRowExpand?.(rowId)}
    >
      {expanded ? '－' : '＋'}
    </Button>
  );
}

interface DetailPhaseRow {
  id: string;
  phaseType: string;
  period: string;
  phaseIdName: string;
  subPhaseIdIdName: string;
  refIdName: string;
  mentorId1Name: string;
  mentorId2Name: string;
  clinicalIdName: string;
  clinicalId2Name: string;
  clinicalId3Name: string;
  wardIdName: string;
  formDone: string;
  formTodo: string;
  formDonePercentage: string;
  examDone: string;
  examTodo: string;
  examDonePercentage: string;
  isActive: string;
}

function toDetailRows(list: unknown): DetailPhaseRow[] {
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null)
    .map((entry, index) => ({
      id: String(entry['id'] ?? `d-${index + 1}`),
      phaseType: String(entry['phaseType'] ?? ''),
      period: String(entry['period'] ?? ''),
      phaseIdName: String(entry['phaseIdName'] ?? ''),
      subPhaseIdIdName: String(entry['subPhaseIdIdName'] ?? ''),
      refIdName: String(entry['refIdName'] ?? ''),
      mentorId1Name: String(entry['mentorId1Name'] ?? ''),
      mentorId2Name: String(entry['mentorId2Name'] ?? ''),
      clinicalIdName: String(entry['clinicalIdName'] ?? ''),
      clinicalId2Name: String(entry['clinicalId2Name'] ?? ''),
      clinicalId3Name: String(entry['clinicalId3Name'] ?? ''),
      wardIdName: String(entry['wardIdName'] ?? ''),
      formDone: String(entry['formDone'] ?? ''),
      formTodo: String(entry['formTodo'] ?? ''),
      formDonePercentage: String(entry['formDonePercentage'] ?? ''),
      examDone: String(entry['examDone'] ?? ''),
      examTodo: String(entry['examTodo'] ?? ''),
      examDonePercentage: String(entry['examDonePercentage'] ?? ''),
      isActive: String(entry['isActive'] ?? '1'),
    }));
}

/** genPeriod（planInstList_js.jsp:230-281）：phaseType 縮排＋非啟用 `(己停用)` text-danger。 */
function periodCell(entry: DetailPhaseRow): ReactElement {
  const period = entry.isActive === '1' ? entry.period : `${entry.period}(己停用)`;
  let wrapperClass = '';
  let strong = false;
  if (entry.phaseType === 'Plan' || entry.phaseType === 'Group') {
    strong = true;
  } else if (entry.phaseType === 'Phase') {
    wrapperClass = 'pl-4';
  } else if (
    entry.phaseType === 'SubPhase' ||
    entry.phaseType === 'community' ||
    entry.phaseType.startsWith('RefElective')
  ) {
    wrapperClass = 'pl-8';
  }
  const content = <small>{period}</small>;
  return <span className={wrapperClass}>{strong ? <strong>{content}</strong> : content}</span>;
}

/** genPhaseIdName/genSubPhaseIdName（:283-324）。 */
function phaseNameCell(entry: DetailPhaseRow): ReactElement {
  if (entry.phaseIdName !== '') {
    if (entry.phaseType === 'community') {
      return (
        <span className="text-green-600">
          <strong>
            {entry.phaseIdName}
            {entry.refIdName === '' ? '' : `(${entry.refIdName})`}
          </strong>
        </span>
      );
    }
    if (entry.phaseType === 'Plan' || entry.phaseType === 'Group' || entry.phaseType === 'Phase') {
      return <strong>{entry.phaseIdName}</strong>;
    }
    return <span>&nbsp;</span>;
  }
  const sub = entry.refIdName === '' ? entry.subPhaseIdIdName : `${entry.subPhaseIdIdName}(${entry.refIdName})`;
  return <strong>{sub}</strong>;
}

/** 明細展開的子內容（row.child；樣板＝planInstList.jsp:66-124，列渲染 :417-531）。 */
export function PlanInstDetailRows({
  pageId,
  row,
  uiKey,
  ctx,
}: {
  pageId: string;
  row: Row;
  uiKey: string;
  ctx: BehaviorFlowContext;
}): ReactElement {
  const dataSource = useDataSource();
  const dialogs = ctx.dialogs;
  const details = toDetailRows(row['planInstDetails']);
  // isMentor/isDeptMan＝getPlanInstReportAjax 回的旗標（雛形 admin 视角＝true，D9）。
  const isMentor = true;
  const isDeptMan = true;
  const [formsOpen, setFormsOpen] = useState(false);

  /** activePlan（:382-415）：confirm→update（列上 planInstList/planInstDetails 同步）→reShowDetail。 */
  const activePlan = (entry: DetailPhaseRow) => {
    void (async () => {
      const isActiveStr = entry.isActive === '1' ? '停用' : '啟用';
      const nextIsActive = entry.isActive === '1' ? '0' : '1';
      const confirmed = await dialogs.confirm(
        `是否確定${isActiveStr}期程[${entry.phaseIdName || entry.subPhaseIdIdName}]?`, // :383
      );
      if (!confirmed) {
        return;
      }
      const mergeIs = (list: unknown) =>
        Array.isArray(list)
          ? (list as Row[]).map((item) =>
              String(item['id'] ?? '') === entry.id ? { ...item, isActive: nextIsActive } : item,
            )
          : list;
      try {
        await dialogs.processing.wrap(
          dataSource.update(pageId, String(row.id ?? ''), {
            planInstList: mergeIs(row['planInstList']),
            planInstDetails: mergeIs(row['planInstDetails']),
          }),
        );
        ctx.reload();
        await dialogs.info(t('state.saveSuccess')); // showInfoDialog(response.desc)
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof Error ? cause.message : t('error.generic'));
      }
    })();
  };

  /** openFormList（:656-678）→`/form/planFormList?phaseInstId=..`：跨頁參數相依→提示呈現。 */
  const openFormList = (phaseInstId: string) => {
    void dialogs.alert(
      `開啟計畫表單查詢：/form/planFormList?phaseInstId=${phaseInstId}【雛形值待實機校準】`,
    );
  };

  const cellClass = 'border-t border-border p-2';
  return (
    <div className="bg-surface-soft p-3" data-ui-id={`${pageId}-row-${uiKey}-detail-body`}>
      <table
        className="w-full border-collapse text-sm"
        data-ui-id={`${pageId}-row-${uiKey}-detail-table`}
      >
        <thead>
          <tr className="border-b border-border-strong">
            <th rowSpan={2} className="w-[30px] p-2" />
            <th rowSpan={2} className="p-2 text-center">訓練期間</th>
            <th rowSpan={2} className="p-2 text-center">訓練階段</th>
            <th rowSpan={2} className="p-2 text-center">階段導師</th>
            <th rowSpan={2} className="p-2 text-center">臨床教師</th>
            <th rowSpan={2} className="p-2 text-center">訓練地點</th>
            <th colSpan={2} className="p-2 text-center">
              <a
                data-ui-id={`${pageId}-row-${uiKey}-detail-form-header-link`}
                className={`${LINK_TEXT_CLASS} cursor-pointer`}
                onClick={() => setFormsOpen(true)}
              >
                表單
              </a>
            </th>
            <th colSpan={2} className="p-2 text-center">測驗</th>
          </tr>
          <tr className="border-b border-border-strong">
            <th className="p-2 text-center">完成份數</th>
            <th className="p-2 text-center">完成度</th>
            <th className="p-2 text-center">完成數</th>
            <th className="p-2 text-center">完成度</th>
          </tr>
        </thead>
        <tbody>
          {details.map((entry) => (
            <tr key={entry.id} className="border-b border-border">
              <td className={`${cellClass} text-center`}>
                <Button
                  uiId={`${pageId}-row-${uiKey}-detail-active-${entry.id}`}
                  variant={entry.isActive === '1' ? 'danger' : 'primary'}
                  size="sm"
                  type="button"
                  dataEffect={`update:${pageId}`}
                  onClick={() => activePlan(entry)}
                >
                  {entry.isActive === '1' ? '停用' : '啟用'}
                </Button>
              </td>
              <td className={cellClass}>{periodCell(entry)}</td>
              <td className={cellClass}>{phaseNameCell(entry)}</td>
              <td className={cellClass}>
                {[entry.mentorId1Name, entry.mentorId2Name]
                  .filter((part) => part !== '')
                  .join(' / ')}
              </td>
              <td className={cellClass}>
                {[entry.clinicalIdName, entry.clinicalId2Name, entry.clinicalId3Name]
                  .filter((part) => part !== '')
                  .join(' / ')}
              </td>
              <td className={cellClass}>{entry.wardIdName}</td>
              {/* isDeptMan||isMentor→openFormList 連結（:447-452）；否則純文字。 */}
              <td className={`${cellClass} text-center`}>
                {isDeptMan || isMentor ? (
                  <a
                    data-ui-id={`${pageId}-row-${uiKey}-detail-form-link-${entry.id}`}
                    className={`${LINK_TEXT_CLASS} cursor-pointer`}
                    onClick={() => openFormList(entry.id)}
                  >
                    {`${entry.formDone}/${entry.formTodo}`}
                  </a>
                ) : (
                  `${entry.formDone}/${entry.formTodo}`
                )}
              </td>
              <td className={`${cellClass} text-right`}>
                {showPercentageOf(entry.formDonePercentage)}
              </td>
              <td className={`${cellClass} text-center`}>{`${entry.examDone}/${entry.examTodo}`}</td>
              <td className={`${cellClass} text-center`}>
                {showPercentageOf(entry.examDonePercentage)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PlanInstFormListModal
        pageId={pageId}
        row={row}
        uiKey={uiKey}
        open={formsOpen}
        onClose={() => setFormsOpen(false)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 學員表單明細 modal（planInstModalFormList.jsp:5-44 #formModal）
// ---------------------------------------------------------------------------

interface PlanInstFormEntry {
  formKey: string;
  formTemplateId: string;
  formTemplateName: string;
  formTargetTypeIdName: string;
  formTargetTypeDetailIdName: string;
  formTargetId: string;
  phaseInstId: string;
  refTable: string;
  refId: string;
  formId: string;
  isDone: string;
  dispatchType: string;
}

interface PlanInstFormGroup {
  period: string;
  phaseIdName: string;
  subPhaseIdIdName: string;
  formList: PlanInstFormEntry[];
}

function formGroupsOf(row: Row | null): PlanInstFormGroup[] {
  const list = row?.['planInstForms'];
  if (!Array.isArray(list)) {
    return [];
  }
  return (list as Row[]).map((entry) => ({
    period: String(entry['period'] ?? ''),
    phaseIdName: String(entry['phaseIdName'] ?? ''),
    subPhaseIdIdName: String(entry['subPhaseIdIdName'] ?? ''),
    formList: (Array.isArray(entry['formList']) ? (entry['formList'] as Row[]) : []).map((form) => ({
      formKey: String(form['formKey'] ?? ''),
      formTemplateId: String(form['formTemplateId'] ?? ''),
      formTemplateName: String(form['formTemplateName'] ?? ''),
      formTargetTypeIdName: String(form['formTargetTypeIdName'] ?? ''),
      formTargetTypeDetailIdName: String(form['formTargetTypeDetailIdName'] ?? ''),
      formTargetId: String(form['formTargetId'] ?? ''),
      phaseInstId: String(form['phaseInstId'] ?? ''),
      refTable: String(form['refTable'] ?? ''),
      refId: String(form['refId'] ?? ''),
      formId: String(form['formId'] ?? ''),
      isDone: String(form['isDone'] ?? ''),
      dispatchType: String(form['dispatchType'] ?? ''),
    })),
  }));
}

/** genFormText（:112-127）：chip 文字（`名稱-類型(明細) `，括號空字串照實）。 */
function formChipLabel(form: PlanInstFormEntry): string {
  let text = form.formTemplateName;
  if (form.formTargetTypeIdName !== '') {
    text += `-${form.formTargetTypeIdName}`;
  }
  text += `(${form.formTargetTypeDetailIdName}) `;
  return text;
}

/** actions 欄的「表單明細」鈕（planInstList_js.jsp:70-78；點擊開 #formModal 同款 modal）。 */
export function PlanInstFormsOpener({
  pageId,
  row,
  uiKey,
}: {
  pageId: string;
  row: Row;
  uiKey: string;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        uiId={`${pageId}-row-${uiKey}-forms`}
        variant="default"
        size="sm"
        type="button"
        onClick={() => setOpen(true)}
      >
        表單明細
      </Button>
      <PlanInstFormListModal
        pageId={pageId}
        row={row}
        uiKey={uiKey}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

/** 學員表單明細 modal（#formModal；title＝`{studentName} 表單明細 `，:92）。 */
export function PlanInstFormListModal({
  pageId,
  row,
  uiKey,
  open,
  onClose,
}: {
  pageId: string;
  row: Row;
  uiKey: string;
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const dialogs = useDialogs();
  const [generateForm, setGenerateForm] = useState<PlanInstFormEntry | null>(null);
  const studentName = String(row['studentIdName'] ?? '');
  const groups = formGroupsOf(row);

  /** showFillForm（:238-249）→window.open：跨頁參數相依→提示呈現（B4-1 先例）。 */
  const showFillForm = (form: PlanInstFormEntry) => {
    void dialogs.alert(
      `開啟填寫頁：form/prompt/fillForm?isWindowOpen=true&formKey=${form.formKey}&fromPage=classForm&refTable=${form.refTable}&refId=${form.refId}【雛形值待實機校準】`,
    );
  };

  return (
    <>
      <ModalFrame
        uiId={`${pageId}-form-detail-dialog-${uiKey}`}
        open={open}
        title={`${studentName} 表單明細 `}
        size="lg"
        onClose={onClose}
      >
        <div className="space-y-3">
          {/* :21 說明列逐字（表單狀態：未發出/進行中/已完成；可發送表單＋external-link）。 */}
          <p data-ui-id={`${pageId}-form-detail-legend`} className="text-sm">
            表單狀態：<span className="text-ink">未發出</span>、<span className="text-red-600">進行中</span>、
            <span className="text-green-600">已完成</span>；<span className="text-ink">可發送表單</span>
            <span aria-hidden>➤</span>
          </p>
          <table className="w-full border-collapse text-sm" data-ui-id={`${pageId}-form-detail-table`}>
            <thead>
              <tr className="border-b border-border-strong">
                <th className="w-1/5 p-2 text-left">訓練期間</th>
                <th className="w-1/5 p-2 text-left">訓練階段</th>
                <th className="w-[15%] p-2 text-left">訓練科別</th>
                <th className="w-[45%] p-2 text-left">表單名稱</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, groupIndex) => (
                <tr key={`${group.period}-${groupIndex}`} className="border-b border-border">
                  <td className="p-2">{group.period}</td>
                  <td className="p-2">{group.phaseIdName}</td>
                  <td className="p-2">{group.subPhaseIdIdName}</td>
                  <td className="p-2">
                    {group.formList.map((form, formIndex) => {
                      const chipClass =
                        form.formId === ''
                          ? 'border-border text-ink-muted'
                          : form.isDone === '1'
                            ? 'border-green-600 text-green-700'
                            : 'border-red-600 text-red-600';
                      const clickable = form.formId !== '';
                      return (
                        <div key={formIndex} className="mb-1 flex items-center gap-2">
                          <button
                            type="button"
                            data-ui-id={`${pageId}-form-detail-chip-${formIndex}`}
                            className={`rounded border px-2 py-0.5 text-xs ${chipClass} ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                            onClick={clickable ? () => showFillForm(form) : undefined}
                          >
                            {formChipLabel(form)}
                          </button>
                          {/* genFormTextExtra（:160-170）：dispatchType 1/2→發送表單圓鈕。 */}
                          {form.dispatchType === '1' || form.dispatchType === '2' ? (
                            <Button
                              uiId={`${pageId}-form-detail-dispatch-${formIndex}`}
                              variant="default"
                              size="sm"
                              type="button"
                              onClick={() => setGenerateForm(form)}
                            >
                              ➤ 發送表單
                            </Button>
                          ) : null}
                        </div>
                      );
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ModalFrame>
      <PlanInstGenerateFormModal
        pageId={pageId}
        uiKey={uiKey}
        form={generateForm}
        onClose={() => setGenerateForm(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 產表單 modal（#generateFormModal＋templateForFiller.jsp 共用表）
// ---------------------------------------------------------------------------

/**
 * 簽核流程表（templateForFiller.jsp:31-66 formFlowListTable 鏡像；fillChoiceModal 同用，
 * 批次 15 Wave 2 再引入）。
 */
export function FormFlowListTable({ uiId }: { uiId: string }): ReactElement {
  const [fillers, setFillers] = useState<Record<number, string>>({});
  const columns = [
    { key: 'flow', header: '流程', align: 'center' as const },
    { key: 'flowRoleName', header: '角色', align: 'center' as const },
    { key: 'account', header: '填寫者', align: 'center' as const },
    { key: 'doctor', header: '指導醫師/教師(非必填)', align: 'center' as const },
  ];
  const anyPool = [...TRAINING_MENTOR_OPTIONS, ...TRAINING_CLINICAL_OPTIONS, ...TRAINING_STUDENT_OPTIONS];
  return (
    <Table
      uiId={uiId}
      columns={columns}
      rows={TRAINING_FORM_FLOW_SEED}
      rowKey={(_row: Row, index: number) => `flow-${index}`}
      emptyText="查無符合的資料"
      renderCell={(row: Row, columnKey: string) => {
        const index = TRAINING_FORM_FLOW_SEED.indexOf(row);
        if (columnKey === 'flow') {
          return String(index + 1);
        }
        if (columnKey === 'flowRoleName') {
          return String(row['flowRoleName'] ?? '');
        }
        if (columnKey === 'account') {
          const flowRole = String(row['flowRole'] ?? '');
          if (flowRole === 'Student') {
            // setDisabledSelect（templateForFiller.jsp:174-190）：鎖定本人。
            return String(row['accountName'] ?? '');
          }
          return (
            <Select
              uiId={`${uiId}-select-${index}`}
              value={fillers[index] ?? String(row['accountId'] ?? '')}
              onChange={(next) => setFillers((current) => ({ ...current, [index]: next }))}
              options={[{ value: '-1', label: '請選擇' }, ...anyPool]}
            />
          );
        }
        if (columnKey === 'doctor') {
          // genFormFlowDoctorRow（templateForFiller.jsp:72-78）：Student 才有，其餘 nbsp。
          if (String(row['flowRole'] ?? '') !== 'Student') {
            return <span>&nbsp;</span>;
          }
          return (
            <Select
              uiId={`${uiId}-doctor-${index}`}
              value={fillers[100 + index] ?? ''}
              onChange={(next) => setFillers((current) => ({ ...current, [100 + index]: next }))}
              options={[{ value: '', label: '' }, ...anyPool]}
            />
          );
        }
        return '';
      }}
    />
  );
}

/** 產表單 modal（#generateFormModal :46-87；showGenerateForm :145-183／generateForm :185-236）。 */
export function PlanInstGenerateFormModal({
  pageId,
  uiKey,
  form,
  onClose,
}: {
  pageId: string;
  uiKey: string;
  form: PlanInstFormEntry | null;
  onClose: () => void;
}): ReactElement {
  const dialogs = useDialogs();
  const [dueDate, setDueDate] = useState('');
  const open = form !== null;
  return (
    <ModalFrame
      uiId={`${pageId}-generate-form-dialog-${uiKey}`}
      open={open}
      title={form?.formTemplateName ?? ''}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button
            uiId={`${pageId}-generate-form-cancel-${uiKey}`}
            variant="default"
            size="sm"
            type="button"
            onClick={onClose}
          >
            關閉
          </Button>
          <Button
            uiId={`${pageId}-generate-form-submit-${uiKey}`}
            variant="primary"
            size="sm"
            type="button"
            onClick={() => {
              void (async () => {
                // generateForm（:201-249）：填寫者空白→0（允許空白不填，20180402）；
                // 雛形無真後端→成功訊息（成功路徑 showInfoDialog(desc) 的雛形值）。
                await dialogs.info(t('state.saveSuccess'));
                onClose();
              })();
            }}
          >
            產生表單
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <label className="text-sm">應完成日</label>
          <div className="w-40">
            <TextInput
              uiId={`${pageId}-generate-form-due-date-${uiKey}`}
              value={dueDate}
              maxLength={10}
              onChange={(next) => setDueDate(next)}
            />
          </div>
        </div>
        <FormFlowListTable uiId={`${pageId}-generate-form-flow-table-${uiKey}`} />
      </div>
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 匯入區（planInstList.jsp:49-81 #importForm；complexBody 由 PlanInstListBody 掛進獨立 form）
// ---------------------------------------------------------------------------

/** tsghImportRow 顯隱（批次 49 Lead 裁決 6）：jobTypeId 值 '1'（系統管理）或空字串視為
 * 隱藏，其餘顯示——對齊 legacy 初始態 display:none（planInstFormSearchPlanInst.jsp:42-46
 * 的整數分支：val==3/225 顯示；雛形查詢 select 只有 '1' 一個可選項，'3'/'225' 與其它值
 * 在 DOM 不可達，故裁決採反向判法；與 §6-5 測試對齊，不可達分支由本 helper 斷言）。 */
export function showTsghImportRowOf(jobTypeId: string): boolean {
  return jobTypeId !== '1' && jobTypeId !== '';
}

/** jobTypeId → data-isgroup（jobType.data('isgroup') 鏡像；範本下載驗證用，
 * planInstList_js.jsp:582）。查無此值（含空字串）回 '0'。 */
export function jobTypeIsGroupOf(jobTypeId: string): string {
  return TRAINING_JOB_TYPE_OPTIONS.find((option) => option.value === jobTypeId)?.isGroup ?? '0';
}

/** 匯入區（importPlanInst :507-570／myDownloadTemplate :576-616 鏡像）。
 *
 * 批次 49 起掛在 PlanInstListBody 的獨立 importForm 內（planInstList.jsp:49-81 的
 * form method=POST id=importForm action="" 鏡像；泛用模板時代的 bulkActions 容器已棄用）。
 * uploadFile 是契約欄位（name=uploadFile，legacy #importForm #uploadFile :519 以 id 讀值），
 * 由 body 持有值（C5-h：file drive 後檔名要進 payload），本元件只收現值＋回寫回呼。
 *
 * 雛形限制：legacy 讀查詢表單 `#searchPlanInstForm #planTargetId/phaseTargetId` 與
 * jobType.data('isgroup')；雛形查詢下拉為 0 options（契約權威，OPTION_SET_OVERRIDES）→
 * 匯入區自帶計畫/組別選單（種子池，planTargetId 預設 -1）＋isGroup 以查詢 jobTypeId 查表
 * 【雛形值待實機校準】。
 */
export function PlanInstImportToolbar({
  pageId,
  jobTypeId,
  uploadFileName,
  onUploadFileNameChange,
  onImported,
}: {
  pageId: string;
  /** 查詢表單 jobTypeId 現值（tsghImportRow 顯隱＋範本下載 isGroup 判斷）。 */
  jobTypeId: string;
  /** 契約 uploadFile 欄現值（檔名；C5-h 接線由 body 持有）。 */
  uploadFileName: string;
  onUploadFileNameChange: (name: string) => void;
  /** 匯入成功後的清單刷新（legacy doQuery，:556-562）。 */
  onImported: () => void;
}): ReactElement {
  const dialogs = useDialogs();
  const [planTargetId, setPlanTargetId] = useState('-1');
  const [phaseTargetId, setPhaseTargetId] = useState('-1');
  // #tsghImportRow（planInstFormSearchPlanInst.jsp:42-46）：批次 49 裁決 6——
  // 值 '1'（系統管理）或空字串視為隱藏，其餘顯示（legacy 初始態 display:none）。
  const showTsghRow = showTsghImportRowOf(jobTypeId);
  const planTargetLabel =
    COURSE_CLAZZ_PLAN_TARGET_OPTIONS.find((option) => option.value === planTargetId)?.label ?? planTargetId;
  const phaseTargetLabel =
    TRAINING_PHASE_TARGET_OPTIONS.find((option) => option.value === phaseTargetId)?.label ?? phaseTargetId;
  // jobType.data('isgroup')：legacy 取查詢表單選中職類的 data-isgroup（:582）；
  // 雛形以查詢 jobTypeId 查種子池（初始 '1'＝系統管理 isGroup 0）【雛形值待實機校準】。
  const isGroup = jobTypeIsGroupOf(jobTypeId);

  const runImport = (importType: 0 | 1) => {
    void (async () => {
      // ① planTarget 檢查（:522-525）：`請選擇 訓練計畫`（plan.target spring code；showInfoDialog）。
      if (planTargetId === '' || planTargetId === '-1') {
        await dialogs.info('請選擇 訓練計畫');
        return;
      }
      // ② uploadFile 檢查（:528-531）：`請選擇匯入的檔案`（showInfoDialog）。
      if (uploadFileName === '') {
        await dialogs.info('請選擇匯入的檔案');
        return;
      }
      // ③ confirm（:533-535）：`訓練計畫 : {text}<br/><br/>是否確定要匯入?`。
      const confirmed = await dialogs.confirm(`訓練計畫 : ${planTargetLabel}\n\n是否確定要匯入?`);
      if (!confirmed) {
        return;
      }
      // ④ 雛形無真後端（批次 14 academicMOM doImport 先例）：成功訊息＋清檔＋reload＋0 data-effect。
      //    legacy 成功：common.showAlertDialog(desc)＋doQuery（:556-562）；:558
      //    `$("#modalPlanTargetDialog").modal("hide")`＝無此 modal 的 copy-paste＝
      //    legacy-known-issue 照實不畫。importType 只影響 URL（training/ vs tsgh/planInst/…）。
      await dialogs.info('匯入成功（雛形不處理檔案內容）【雛形值待實機校準】');
      onUploadFileNameChange('');
      onImported();
      void importType;
    })();
  };

  const downloadTemplate = () => {
    void (async () => {
      // myDownloadTemplate（:576-616）：planTarget 空→`請選擇計畫訓練`（字面值 alert）；
      // isGroup==1 且組別空→`請選擇組別 `（尾空格）；confirm 的組別列只在 phaseTarget>0 時串接
 // （:598-600 條件）。
      if (planTargetId === '' || planTargetId === '-1') {
        await dialogs.alert('請選擇計畫訓練');
        return;
      }
      if (isGroup === '1' && (phaseTargetId === '' || phaseTargetId === '-1')) {
        await dialogs.alert('請選擇組別 ');
        return;
      }
      const confirmed = await dialogs.confirm(
        `訓練計畫 : ${planTargetLabel}${phaseTargetId !== '' && phaseTargetId !== '-1' ? `\n 組別 : ${phaseTargetLabel}` : ''}\n\n是否確定要下載該計畫之範本?`,
      );
      if (!confirmed) {
        return;
      }
      // Blob 下載（批次 9 downloadTemplateBlob 先例【雛形值待實機校準】）。
      if (typeof URL.createObjectURL !== 'function') {
        return;
      }
      const blob = new Blob(
        ['React 雛形佔位範本（legacy 來源：training/planInst/downloadTemplate?planTargetId=…，實機校準後由真後端提供）'],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = '訓練期程匯入範本.xlsx';
      anchor.click();
      URL.revokeObjectURL(url);
    })();
  };

  return (
    <span data-ui-id={`${pageId}-import-actions`} className="inline-flex flex-col items-start gap-2">
      {/* 匯入期程資料 label（planInstList.jsp:52；legacy 是 label 元素，normalize 後與
          控制項同層配對——批次 49 起改 label 元素鏡像）。 */}
      {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字複製 legacy 的純文字 label（控制項是兄弟節點；LegacyContractFields 同款）。 */}
      <label className="text-sm font-bold text-ink">匯入期程資料：</label>
      {/* 計畫/組別選單（雛形自帶；legacy 讀查詢表單 `#searchPlanInstForm #planTargetId`，
          查詢下拉 0 options（OPTION_SET_OVERRIDES）→ 維持現狀提供可選性（規格 §4.1/§8-2）
          【雛形值待實機校準】）。react-only 元素＝匯入 toolbar 自帶選單的既有多元素帳。 */}
      <Select
        uiId={`${pageId}-import-plan-target`}
        value={planTargetId}
        onChange={(next) => setPlanTargetId(next)}
        options={[{ value: '-1', label: '請選擇' }, ...COURSE_CLAZZ_PLAN_TARGET_OPTIONS]}
      />
      <Select
        uiId={`${pageId}-import-phase-target`}
        value={phaseTargetId}
        onChange={(next) => setPhaseTargetId(next)}
        options={[{ value: '-1', label: '請選擇' }, ...TRAINING_PHASE_TARGET_OPTIONS]}
      />
      {/* uploadFile（:56 契約欄位 name=uploadFile；legacy accept=.xlsx，accept 不在
          normalize 屬性 allowlist，鏡像不影響比對）。 */}
      <input
        type="file"
        name="uploadFile"
        accept=".xlsx"
        data-ui-id={`${pageId}-import-file`}
        className="text-xs"
        onChange={(event) => {
          onUploadFileNameChange(event.target.files?.[0]?.name ?? event.target.value);
        }}
      />
      <Button
        uiId={`${pageId}-import-submit`}
        variant="primary"
        size="sm"
        type="button"
        onClick={() => runImport(0)}
      >
        期程匯入
      </Button>
      {/* #tsghImportRow（:63-67）：legacy 是 div style=display:none → normalize 保留 @hidden
          元素；JS 顯示時拔掉 display:none → div（class only）被剝除、按鈕浮到 form 層。
          鏡像：div 恆在、按鈕恆在，hidden 屬性依裁決 6 顯隱切換（show 時無 hidden＝剝除）。 */}
      <div {...(showTsghRow ? {} : { hidden: true })}>
        <Button
          uiId={`${pageId}-import-submit-tsgh`}
          variant="primary"
          size="sm"
          type="button"
          onClick={() => runImport(1)}
        >
          訓練官及住院醫師用每月期程匯入
        </Button>
      </div>
      {/* 使用說明（:71-72 label＋:74-75 範本下載鈕＋:77 li 逐字；legacy 是 label 元素）。 */}
      <span className="inline-flex items-center gap-1 text-sm text-ink">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字複製 legacy 的純文字 label（使用說明: 後接範本下載鈕）。 */}
        <label>使用說明:</label>
        <Button
          uiId={`${pageId}-import-template`}
          variant="default"
          size="sm"
          type="button"
          onClick={downloadTemplate}
        >
          範本下載
        </Button>
      </span>
      <ul className="list-disc pl-5 text-xs text-ink">
        <li>
          請勿修改已開始的訓練階段(例如：將A階段改成B階段)，
          <br />
          這將會使計畫各階段表單及其資料對應發生錯誤。
        </li>
      </ul>
    </span>
  );
}
